import React, { useRef, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import {
  LayoutGrid,
  MoreVertical,
  Paperclip,
  Plus,
  Presentation,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import type { ReportsLayoutMode } from 'components/Reports/hooks/useReportsLayoutMode';
import SitrepAutoRefreshControls, {
  type SitrepAutoRefreshControlsHandle,
} from 'components/Reports/SitrepAutoRefreshControls';
import ReportExportMenuItems from 'components/Reports/ReportsExportButton';
import SitrepLayoutMenuItems from 'components/Reports/SitrepLayoutMenuItems';
import {
  keys,
  useRemoveSitrepV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import RequestApprovalConfirmationModal from 'components/Reports/RequestApprovalConfirmationModal';
import RequestApprovalSuccessModal from 'components/Reports/RequestApprovalSuccessModal';
import AorApprovalSettingsDialog from 'components/Reports/AorApprovalSettingsDialog';
import {
  useAorReportApprovalPolicy,
  usePublishAorReport,
} from 'components/Reports/hooks/useAorReportApprovalPolicy';
import { ITEM_TYPES } from 'constants/itemType';
import { useGetPositionsForIncidentId } from 'hooks/incident/useRoster';
import {
  useEligibleApprovalReviewers,
  useRequestApprovals,
} from 'hooks/useApprovals';

const POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL = [
  'Situation Unit Leader',
  'Planning Section Chief',
  'Incident Commander',
];

type DraftSitrepActionsMenuProps = {
  sitrep: GetSitrepsResponse;
  layoutMode: ReportsLayoutMode;
  onLayoutModeChange: (mode: ReportsLayoutMode) => void;
  showSubmitForReview?: boolean;
  onAddSection?: () => void;
  onAddFiles?: () => void;
  /** Controlled open state, used by the Reports tour to spotlight menu items. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPresent?: () => void;
  onQuadChart?: () => void;
  presentDisabled?: boolean;
};

/** Kebab menu for draft report header actions (authoring, submit, delete). */
const DraftSitrepActionsMenu: React.FC<DraftSitrepActionsMenuProps> = ({
  sitrep,
  layoutMode,
  onLayoutModeChange,
  showSubmitForReview = true,
  onAddSection,
  onAddFiles,
  open,
  onOpenChange,
  onPresent,
  onQuadChart,
  presentDisabled = false,
}) => {
  const aorText = useAorText();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRefreshInterval, setShowRefreshInterval] = useState(false);
  const [refreshSaving, setRefreshSaving] = useState(false);
  const refreshControlsRef = useRef<SitrepAutoRefreshControlsHandle>(null);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showApprovalSettings, setShowApprovalSettings] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [pendingSuccess, setPendingSuccess] = useState(false);

  const removeSitrep = useRemoveSitrepV2();
  const requestApprovals = useRequestApprovals(keys.base, {
    suppressSuccessToast: true,
    deferQueryInvalidation: true,
  });
  const aorEligibleReviewersQuery = useEligibleApprovalReviewers({
    itemId: sitrep.item_id,
    enabled: !sitrep.incident_id,
  });
  const incidentPositionsQuery = useGetPositionsForIncidentId(
    sitrep.incident_id || ''
  );
  const filteredAorReviewers = aorEligibleReviewersQuery.data ?? [];
  const isAorReport = !!sitrep.aor_id && !sitrep.incident_id;
  const approvalPolicyQuery = useAorReportApprovalPolicy(
    isAorReport ? sitrep.aor_id : null
  );
  const policySteps = approvalPolicyQuery.data?.resolvedSteps ?? [];
  const publishesDirectly = sitrep.incident_id
    ? true
    : isAorReport && approvalPolicyQuery.isSuccess && policySteps.length === 0;
  const publishReport = usePublishAorReport();

  const reportLabel = sitrep.name?.trim() || 'this draft report';

  const handleConfirmDelete = async () => {
    try {
      await removeSitrep.mutateAsync({ sitrepId: sitrep.id });
      setShowDeleteConfirm(false);
      toast.success('Draft report deleted');
    } catch {
      toast.error('Failed to delete draft report');
    }
  };

  const handleConfirmApproval = async () => {
    let reviewers: { reviewerItemId: string }[];

    if (sitrep.incident_id) {
      const positions = incidentPositionsQuery.data;
      reviewers = POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL.map(
        (positionName) => {
          const position = positions?.find(
            (p) => p.name === positionName && !p.isResourcePosition
          );
          if (!position) {
            return null;
          }
          return { reviewerItemId: position.item_id };
        }
      ).filter((r): r is { reviewerItemId: string } => r !== null);
    } else {
      reviewers = filteredAorReviewers.map((r) => ({
        reviewerItemId: r.reviewerItemId,
      }));

      if (reviewers.length === 0 && policySteps.length === 0) {
        toast.info('No eligible reviewers found. Request sent to you only.');
        setPendingSuccess(true);
        setShowApprovalConfirm(false);
        return;
      }
    }

    await requestApprovals.mutateAsync({
      itemId: sitrep.item_id,
      itemType: ITEM_TYPES.Sitrep,
      reviewers,
      approvalsRequiredInOrder: !!sitrep.incident_id,
      groupId: sitrep.group_guid,
      incidentId: sitrep.incident_id || undefined,
    });
    setPendingSuccess(true);
    setShowApprovalConfirm(false);
  };

  const handleConfirmPublish = async () => {
    try {
      await publishReport.mutateAsync(sitrep.id);
      setShowPublishConfirm(false);
      toast.success('Published as the current report');
    } catch (error) {
      const response = (
        error as { response?: { status?: number; data?: { error?: unknown } } }
      ).response;
      const serverMessage = response?.data?.error;
      toast.error(
        typeof serverMessage === 'string'
          ? serverMessage
          : 'Failed to publish report'
      );
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Draft report actions"
              data-tour="reports-draft-menu"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="tw:h-4 tw:w-4" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="end"
          className="dark tw:w-64"
          onClick={(e) => e.stopPropagation()}
        >
          <SitrepLayoutMenuItems
            layoutMode={layoutMode}
            onLayoutModeChange={onLayoutModeChange}
          />
          {onPresent ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={presentDisabled} onClick={onPresent}>
                <Presentation />
                Presentation mode
              </DropdownMenuItem>
              {onQuadChart ? (
                <DropdownMenuItem
                  disabled={presentDisabled}
                  onClick={onQuadChart}
                >
                  <LayoutGrid />
                  Quad Chart
                </DropdownMenuItem>
              ) : null}
            </>
          ) : null}
          <DropdownMenuSeparator />
          <ReportExportMenuItems sitrep={sitrep} />
          <DropdownMenuSeparator />
          {isAorReport && sitrep.aor_id ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <span className="tw:block">
                      <DropdownMenuItem
                        disabled
                        className="tw:pointer-events-none"
                      >
                        <ShieldCheck />
                        Approval settings
                      </DropdownMenuItem>
                    </span>
                  }
                />
                <TooltipContent className="dark">
                  Upgrade to unlock approval workflows.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {onAddSection && (
            <DropdownMenuItem onClick={onAddSection}>
              <Plus />
              Add Section
            </DropdownMenuItem>
          )}
          {onAddFiles && (
            <DropdownMenuItem
              disabled={!sitrep.incident_id}
              onClick={onAddFiles}
            >
              <Paperclip />
              Add Files
            </DropdownMenuItem>
          )}
          {(onAddSection || onAddFiles) && <DropdownMenuSeparator />}
          <DropdownMenuItem
            onClick={() => {
              onOpenChange?.(false);
              setShowRefreshInterval(true);
            }}
          >
            <RefreshCw />
            Set Refresh Time Interval
          </DropdownMenuItem>
          {(showSubmitForReview ||
            (!!sitrep.incident_id && sitrep.approval_status === 'PENDING')) && (
            <DropdownMenuItem
              data-tour="reports-submit-review"
              disabled={
                requestApprovals.isPending ||
                publishReport.isPending ||
                (!sitrep.incident_id &&
                  isAorReport &&
                  approvalPolicyQuery.isLoading)
              }
              onClick={() => {
                if (publishesDirectly) {
                  setShowPublishConfirm(true);
                  return;
                }
                setShowApprovalConfirm(true);
              }}
            >
              <Send />
              {publishesDirectly ? 'Publish' : 'Submit for review'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            variant="destructive"
            disabled={removeSitrep.isPending}
            onClick={() => {
              setShowDeleteConfirm(true);
            }}
          >
            <Trash2 />
            Delete draft
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={showRefreshInterval}
        onOpenChange={(open) => {
          if (refreshSaving) return;
          setShowRefreshInterval(open);
        }}
      >
        <DialogContent
          className="dark tw:z-[1101] tw:sm:max-w-lg"
          overlayClassName="tw:z-[1100]"
        >
          <DialogHeader>
            <DialogTitle>Set Refresh Time Interval</DialogTitle>
            <DialogDescription>
              Choose how often this report should refresh its draft, and who to
              notify when that draft is updated.
            </DialogDescription>
          </DialogHeader>
          {showRefreshInterval ? (
            <SitrepAutoRefreshControls
              ref={refreshControlsRef}
              sitrep={sitrep}
              persistOnChange={false}
            />
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={refreshSaving}
              onClick={() => setShowRefreshInterval(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={refreshSaving}
              onClick={async () => {
                setRefreshSaving(true);
                const saved = await refreshControlsRef.current?.save();
                setRefreshSaving(false);
                if (saved) {
                  setShowRefreshInterval(false);
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        title="Delete draft report?"
        pending={removeSitrep.isPending}
        onOpenChange={(open) => {
          if (!open && !removeSitrep.isPending) setShowDeleteConfirm(false);
        }}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        description={
          <>
            Are you sure you want to delete &ldquo;{reportLabel}&rdquo;? This
            removes the draft and its sections from Reports. This action cannot
            be undone from the UI.
          </>
        }
      />

      <Dialog
        open={showPublishConfirm}
        onOpenChange={(open) => {
          if (publishReport.isPending) return;
          setShowPublishConfirm(open);
        }}
      >
        <DialogContent
          className="dark tw:z-[1101] tw:sm:max-w-lg"
          overlayClassName="tw:z-[1100]"
        >
          <DialogHeader>
            <DialogTitle>Publish this draft?</DialogTitle>
            <DialogDescription>
              This draft becomes the current report. No approval is required.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={publishReport.isPending}
              onClick={() => setShowPublishConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={publishReport.isPending}
              onClick={() => {
                void handleConfirmPublish();
              }}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RequestApprovalConfirmationModal
        show={showApprovalConfirm}
        onHide={() => setShowApprovalConfirm(false)}
        onConfirm={handleConfirmApproval}
        isSubmitting={requestApprovals.isPending}
        name={sitrep.name}
        createdBy={sitrep.created_by_user_name ?? 'Unknown'}
        createdAt={sitrep.timestamp}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        editedAt={sitrep.updated_at ?? sitrep.timestamp}
        incidentId={sitrep.incident_id}
        groupId={sitrep.group_guid}
        aorReviewers={filteredAorReviewers}
        approvalSteps={policySteps}
        reviewersLoading={isAorReport && approvalPolicyQuery.isLoading}
        positionNames={
          sitrep.incident_id
            ? POSITION_NAMES_FOR_INCIDENT_REPORT_APPROVAL
            : filteredAorReviewers.map((r) => r.displayName)
        }
        fallbackReviewerLabel={
          sitrep.incident_id
            ? undefined
            : aorText(
                'No SITREP reviewers with AOR permissions are configured for this report.'
              )
        }
        onExited={() => {
          if (pendingSuccess) {
            setPendingSuccess(false);
            setShowSuccess(true);
          }
        }}
      />
      {isAorReport && sitrep.aor_id ? (
        <AorApprovalSettingsDialog
          open={showApprovalSettings}
          aorId={sitrep.aor_id}
          onOpenChange={setShowApprovalSettings}
        />
      ) : null}
      <RequestApprovalSuccessModal
        show={showSuccess}
        draftName={sitrep.name}
        editedBy={
          sitrep.updated_by_user_name ??
          sitrep.created_by_user_name ??
          'Unknown'
        }
        incidentId={sitrep.incident_id}
        onHide={() => {
          setShowSuccess(false);
          requestApprovals.invalidateQueries();
        }}
      />
    </>
  );
};

export default DraftSitrepActionsMenu;
